/**
 * The page before you sign in.
 *
 * WHAT WAS HERE BEFORE: nothing. The documents list returns null for a visitor with no session --
 * correctly, because it is a list of a tenant's controlled documents and there is no tenant yet --
 * so docs.cres-intelligence.io answered with a header and an empty white page. The sign-in control
 * is an unlabelled person icon at the far top right, which is findable once somebody has been told.
 *
 * THE STORY IS THE ONE VEEVA TELLS ABOUT GxP DOCUMENTS, at Ed's direction, because it is the story
 * a quality organisation already has in its head when it arrives: controlled content lives in the
 * quality system rather than beside it; the lifecycle is enforced rather than remembered; a printed
 * page says what it is; and the trail is complete enough to hand an inspector. Those are the beats
 * below, in that order.
 *
 * WHAT BOUNDS IT, and this is the harder half: every sentence has to be true of THIS system today.
 * So the lifecycle beat says what our verdict actually does -- the record's state decides whether
 * the text can be edited, and the renderer stamps number, revision, confidentiality and state on
 * every page until the revision is released, which it does, in that order, and the state is omitted
 * once released. It does NOT say periodic review, effective dates, read-and-understand training,
 * expiring controlled copies or Part 11 validation, because this system does not do those and a
 * landing page is a bad place to find that out.
 *
 * The one beat that is ours rather than theirs is co-authoring: Veeva's story has check-out and
 * check-in in it, and ours does not.
 *
 * NO LOCKUP OF ITS OWN. It had one -- logo, ParkourAI, the product -- and the header
 * directly above it carries exactly that, so the screen said it twice, 250 pixels apart, and
 * the second one read as a mistake. The headline is the first thing on the page instead.
 *
 * THE SHAPE IS SHARED WITH REPORTING's. Two apps in one suite reached by two addresses should not
 * look like two companies, so the brand row, the measure, the card row and the persona line are
 * laid out identically in both and only the words differ. They are separate repositories with no
 * shared package between them, so "identically" is maintained by hand, which is worth knowing
 * before changing one of them.
 */
import React from "react";
import { Box, Button, Chip, Paper, Typography } from "@mui/material";
import {
    EditNoteOutlined, StraightenOutlined, KeyboardTabOutlined, TableChartOutlined,
    ImageOutlined, VerticalAlignTopOutlined, ArticleOutlined, FindReplaceOutlined,
    FormatListNumberedOutlined, UploadFileOutlined, GroupsOutlined, RateReviewOutlined,
    ForumOutlined, PersonAddAltOutlined, LockOutlined, VisibilityOutlined,
    HistoryOutlined, RestoreOutlined, VerifiedOutlined, SettingsOutlined,
    PictureAsPdfOutlined, MenuBookOutlined, LinkOutlined, TocOutlined,
    BookmarkBorderOutlined, FileDownloadOutlined, SegmentOutlined, DifferenceOutlined,
    AlternateEmailOutlined, TuneOutlined, BrandingWatermarkOutlined, SuperscriptOutlined,
    StyleOutlined, AccountTreeOutlined, InsertPageBreakOutlined, SpellcheckOutlined, RuleOutlined, ViewColumnOutlined,
    CloudOffOutlined, NumbersOutlined, SearchOutlined, PhotoSizeSelectActualOutlined,
    DashboardCustomizeOutlined,
    ScheduleOutlined, FilterAltOutlined, WrapTextOutlined,
} from "@mui/icons-material";

import useAppContext from "../util/AppContext";
import { Blue3, Blue4, Grey2, Grey3, Grey5, Grey7, Grey17, White } from "../theme/styles";
import { useNavigate } from "react-router-dom";
import { storyFor } from "./featureStories";

/**
 * What this app does, in four parts.
 *
 * ORDERED AS SOMEBODY MEETS IT: you write, the page is a page, and what you may open is not this
 * app's decision. The third one is the one a quality manager asks about first and it is the one a
 * feature list always leaves out.
 */
const DOES = [
    {
        title: "One controlled source",
        text: "A GxP procedure's text belongs to a revision of its record in the quality system — "
            + "not a file on a share, not an attachment in a mail thread. Opening the record opens "
            + "the document, and there is no second copy to reconcile.",
    },
    {
        title: "Authored in place, together",
        text: "Several authors in one document at the same time, each caret where they are. "
            + "Propose a change without making it, accept or reject what somebody else proposed, "
            + "and read back any earlier version. Nothing is checked out, and nothing is merged "
            + "from mail.",
    },
    {
        title: "The lifecycle decides",
        text: "Whether a document can be edited is the record's state, enforced rather than "
            + "remembered. Every page of the PDF carries the document number, the revision and "
            + "your confidentiality mark, and a revision that is not released is watermarked "
            + "with the state it is in.",
    },
    {
        title: "A trail you can hand over",
        text: "Every save is an event with a name and a time, and the versions behind them can be "
            + "read back. Who may open a document is their persona's answer; an outside reviewer "
            + "gets the one document they were given, and nothing else.",
    },
];


/**
 * EVERYTHING THIS APP DOES, AND EVERYTHING IT IS GOING TO.
 *
 * Ed asked for the full catalogue on the landing page. The rule this page was written under still
 * holds -- nothing on it is a promise -- so the way to show unbuilt work is to SAY it is unbuilt,
 * beside each one, rather than to let a planned feature read as a shipped one. A roadmap openly
 * labelled is not a promise; a feature list with the future quietly mixed in is.
 *
 * "Building" means exactly that and nothing looser: it is in the working tree today. If it stops
 * being true it comes off this page, because a status that drifts is worse than no status.
 *
 * EVERY ENTRY WAS CHECKED AGAINST THE PRODUCT, not against memory. The editor's own menus carry
 * "Not built yet" with a reason on each one, and that list is the authority for what is Planned:
 * `grep "Not built yet" src/components/Docs` before touching a status here.
 *
 * RE-CHECKED 2026-10-05, and nine entries were wrong -- all of them in the same direction, which is
 * the direction that matters: shipped work still labelled "On the roadmap". Cross-references, the
 * table of contents, bookmarks, the outline pane, page breaks, word count, grouped history, show
 * changes, page setup, the watermark and the Word export were all built and all still reading as
 * future. The sweep also found the FILE > PAGE SETUP menu entry saying "Not built yet" over a
 * dialog that had been shipped -- the landing page was stale in the same place the product was,
 * because both were written from the same note rather than from the code.
 */
type Status = "built" | "building" | "planned";

type Feature = {
    icon: React.ReactNode;
    name: string;
    text: string;
    status: Status;
};

type Group = { title: string; blurb: string; features: Feature[] };

const ART = { fontSize: 22 };

const CATALOGUE: Group[] = [
    {
        title: "Writing",
        blurb: "What a procedure is made of.",
        features: [
            { status: "built", icon: <EditNoteOutlined sx={ART}/>, name: "Rich text",
              text: "Headings, lists, check-lists, alignment, spacing \u2014 the ordinary business of writing a document." },
            { status: "built", icon: <StraightenOutlined sx={ART}/>, name: "The ruler",
              text: "Indents and hanging indents dragged where you want them, which is how a numbered clause is written." },
            { status: "built", icon: <KeyboardTabOutlined sx={ART}/>, name: "Tab stops",
              text: "Columns that stay lined up when the words in front of them change." },
            { status: "built", icon: <TableChartOutlined sx={ART}/>, name: "Tables",
              text: "Per-edge borders, column widths, a pinned header row and a minimum row height for the signature block \u2014 all drawn the same way in the PDF." },
            { status: "built", icon: <ImageOutlined sx={ART}/>, name: "Figures",
              text: "Stored as attachments on the record, so a document full of images still opens at the speed of its text." },
            { status: "built", icon: <VerticalAlignTopOutlined sx={ART}/>, name: "Headers and footers",
              text: "Live fields for the document number, revision and state \u2014 so a footer cannot print last year\u2019s revision." },
            { status: "built", icon: <FormatListNumberedOutlined sx={ART}/>, name: "Numbered headings",
              text: "1, 1.1, 1.1.1 \u2014 computed from the document\u2019s shape, never typed, so they cannot go stale." },
            { status: "built", icon: <LinkOutlined sx={ART}/>, name: "Cross-references",
              text: "\u201cAs described in 4.2\u201d, still correct after somebody inserts a section above it." },
            { status: "built", icon: <BookmarkBorderOutlined sx={ART}/>, name: "Bookmarks",
              text: "Point a reference at any passage you choose, not only at a heading." },
            { status: "built", icon: <TocOutlined sx={ART}/>, name: "Table of contents",
              text: "The document\u2019s actual shape rather than a copy of it, so there is nothing to update." },
            { status: "built", icon: <AccountTreeOutlined sx={ART}/>, name: "Outline pane",
              text: "Navigate a long procedure by its structure, with the section you are in marked." },
            { status: "built", icon: <ArticleOutlined sx={ART}/>, name: "Page preview",
              text: "The page breaks as you write, so what you arrange is what prints." },
            { status: "built", icon: <InsertPageBreakOutlined sx={ART}/>, name: "Page breaks",
              text: "Decide where a page ends yourself, and the PDF breaks there too." },
            { status: "built", icon: <FindReplaceOutlined sx={ART}/>, name: "Find and replace",
              text: "Across the whole document, including the parts you are not looking at." },
            { status: "built", icon: <NumbersOutlined sx={ART}/>, name: "Word count",
              text: "For the document, or for what you have selected." },
            { status: "built", icon: <UploadFileOutlined sx={ART}/>, name: "Import from Word",
              text: "Bring an existing procedure in; the import is recorded as its own event." },
            { status: "built", icon: <SuperscriptOutlined sx={ART}/>, name: "Footnotes",
              text: "At the foot of the page the reference landed on, in the PDF as well as on screen, and renumbered when you add one above." },
            { status: "built", icon: <StyleOutlined sx={ART}/>, name: "Named styles",
              text: "Format one heading the way you want it, then update the style to match \u2014 every heading of that level follows, on screen and in the PDF." },
            // ASKED FOR AND BUILT 2026-10-05, reversing an earlier decision not to: a template
            // gallery had been on the list of things deliberately not being built. The shape it
            // took is the one the quality system forces -- a template is a DOCUMENT, flagged, so
            // it inherits an owner, a revision, an approval and a visibility answer rather than
            // needing four new things of its own.
            { status: "built", icon: <DashboardCustomizeOutlined sx={ART}/>, name: "Templates",
              text: "Start from your own SOP or protocol shell instead of a blank page. A template IS a controlled document somebody ticked a box on, so it has an owner, a revision and an approval like everything else." },
            { status: "built", icon: <WrapTextOutlined sx={ART}/>, name: "Text around a figure",
              text: "Put the picture against either margin and the words run down its side \u2014 on screen and in the PDF, which wraps the same paragraphs." },
            // NAMES A CLAUSE, CLAIMS NOTHING FURTHER. The rule at the top of this file is that
            // every sentence must be true of this system today, which is why the page does not
            // mention Part 11 validation. Annex 11 §6 asks for a check on critical data entered
            // by hand; a column type IS such a check, and saying so is a statement about the clause
            // and this feature rather than a claim of compliance.
            { status: "built", icon: <RuleOutlined sx={ART}/>, name: "Column types",
              text: "Say that a column holds dates, numbers, or one of a list, and the cells that do not are marked as you type \u2014 and the document cannot be sent for approval until they are fixed. EU GMP Annex 11 \u00a76 asks for a check on critical data entered by hand; this is where you declare it. Slashed dates are refused on purpose: 01/02/2026 means two different days either side of the Atlantic." },
            // THE WORDING WAS A PROMISE NOTHING COULD KEEP. It said a dictionary would stop the
            // browser underlining your part numbers; a page cannot add a word to the browser's
            // spell-checker, because there is no API for it. What replaced it is the half that is
            // both possible and more useful -- the document's words joined to the approved
            // glossary -- and the sentence now says what the browser keeps doing instead of
            // implying we take it over.
            // SHIPPED 2026-10-07, the last entry in unbuilt.ts that was a genuine gap rather than
            // a decision. The tile is added in the same change, because the splash is the
            // inventory of what this product does and an inventory that lags is one nobody trusts.
            { status: "built", icon: <ViewColumnOutlined sx={ART}/>, name: "Columns",
              text: "One, two or three. The text flows down the first and on into the next, and on to the following page \u2014 the browser and the renderer taking the measure from the same arithmetic." },
            { status: "built", icon: <SpellcheckOutlined sx={ART}/>, name: "The glossary, in the text",
              text: "Terms your Enterprise Glossary has released are marked where they appear, each carrying the definition somebody approved \u2014 so \u201cwhat does this mean here?\u201d is answered from the controlled glossary rather than from whoever is reading. Spell checking stays your browser\u2019s: a page cannot teach it your part numbers, and pretending otherwise would be the wrong promise." },
        ],
    },
    {
        title: "Working together",
        blurb: "Review is a conversation, and it belongs in the record.",
        features: [
            { status: "built", icon: <GroupsOutlined sx={ART}/>, name: "Co-authoring",
              text: "Several people in one document at once, each caret where they are. Nothing is checked out." },
            { status: "built", icon: <RateReviewOutlined sx={ART}/>, name: "Suggestions",
              text: "Propose a change without making it; the author accepts or rejects it." },
            { status: "built", icon: <ForumOutlined sx={ART}/>, name: "Comments",
              text: "Ask about a passage without changing it \u2014 including while the revision is out for approval." },
            { status: "built", icon: <PersonAddAltOutlined sx={ART}/>, name: "Outside reviewers",
              text: "Somebody beyond your company gets the one document they were given, and nothing else." },
            { status: "built", icon: <AlternateEmailOutlined sx={ART}/>, name: "Mentions",
              text: "Type @ in a comment to name somebody, and they are told. You can only name people who can already see the document \u2014 checked when the comment is written, not just in the picker \u2014 and the record keeps which of their roles let them see it." },
        ],
    },
    {
        title: "Finding one",
        blurb: "A quality system with a thousand procedures in it is only as good as its search.",
        features: [
            { status: "built", icon: <SearchOutlined sx={ART}/>, name: "Full-text search",
              text: "Searches the words inside your documents, not just their titles, and returns the sentence the match is in \u2014 across the documents your persona can open, and no others." },
            { status: "built", icon: <PhotoSizeSelectActualOutlined sx={ART}/>, name: "Page-one previews",
              text: "Each document shows its own first page, words and figures in their places, so you recognise it rather than read its number." },
            { status: "built", icon: <FilterAltOutlined sx={ART}/>, name: "Filters and sorting",
              text: "By state, by author, by when it last moved \u2014 with the count beside each filter before you apply it." },
        ],
    },
    {
        title: "Control",
        blurb: "What makes it a controlled document rather than a file.",
        features: [
            { status: "built", icon: <LockOutlined sx={ART}/>, name: "Lifecycle gating",
              text: "Whether you may type is the record\u2019s state, enforced rather than remembered." },
            { status: "built", icon: <VisibilityOutlined sx={ART}/>, name: "Persona visibility",
              text: "Which documents exist for you is the quality system\u2019s answer, not this app\u2019s." },
            { status: "built", icon: <VerifiedOutlined sx={ART}/>, name: "What gets signed",
              text: "An approval covers a hash of the text \u2014 and a document carrying unresolved changes cannot be submitted." },
            { status: "built", icon: <RestoreOutlined sx={ART}/>, name: "Every edit kept",
              text: "Any moment can be read back, not only the ones a periodic snapshot happened to catch." },
            { status: "built", icon: <HistoryOutlined sx={ART}/>, name: "Named versions",
              text: "\u201cIssued for review\u201d is something you can find, rather than a timestamp you have to remember." },
            { status: "built", icon: <SegmentOutlined sx={ART}/>, name: "Grouped history",
              text: "A day of typing reads as a dozen entries rather than two hundred, and a named version is never folded into one." },
            { status: "built", icon: <DifferenceOutlined sx={ART}/>, name: "Show changes",
              text: "What moved between two versions, with the unchanged text dimmed rather than hidden \u2014 a change needs its sentence around it." },
            { status: "built", icon: <ScheduleOutlined sx={ART}/>, name: "Browse by time",
              text: "Drag back through the document\u2019s own moments; named versions and imports are marked along the way." },
            { status: "built", icon: <SettingsOutlined sx={ART}/>, name: "Account policy",
              text: "Decide for yourself whether an unanswered question stops a release." },
        ],
    },
    {
        title: "Output",
        blurb: "What leaves the system.",
        features: [
            { status: "built", icon: <PictureAsPdfOutlined sx={ART}/>, name: "The rendition",
              text: "Every page carries the document number, revision, confidentiality mark and state until release." },
            { status: "built", icon: <BrandingWatermarkOutlined sx={ART}/>, name: "Watermark",
              text: "A revision that is not released says so across every page, on screen and in the PDF, so a printout cannot be mistaken for the controlled version." },
            { status: "built", icon: <FileDownloadOutlined sx={ART}/>, name: "Export to Word",
              text: "The copy suppliers and auditors ask for. Every page of it says UNCONTROLLED COPY, with the number, revision and state." },
            { status: "built", icon: <TuneOutlined sx={ART}/>, name: "Page setup",
              text: "Portrait or landscape and margins on all four sides \u2014 the screen, the print stylesheet and the server-side render reading one set of numbers." },
            { status: "built", icon: <MenuBookOutlined sx={ART}/>, name: "Guides",
              text: "Nine of them, written for the people doing the work and kept honest about what is not built." },
        ],
    },
];

const LABEL: Record<Status, string> = {
    built: "Available",
    building: "Building now",
    planned: "On the roadmap",
};

export default function Splash() {
    const navigate = useNavigate();
    const sessionContext = useAppContext()?.sessionContext;

    // THE SAME CONTROL THE HEADER'S PERSON ICON USES. Setting the flag emits a session update and
    // the navigation bar, which owns the dialog, opens it -- so this button needs no dialog of its
    // own and cannot disagree with that one about whether sign-in is open.
    const signIn = () => sessionContext?.setShowLoginDialog?.(true);

    return (
        <Box sx={{
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            minHeight: "100%", width: "100%", backgroundColor: White,
            // THIS SUITE HAS NO GLOBAL border-box RULE, deliberately -- so a width of 100% with
            // side padding is 100% PLUS the padding, and the page scrolled sideways by exactly
            // 40px on a phone. Set here rather than globally for the reason the suite does not set
            // it globally: a global change reflows every screen in the app.
            boxSizing: "border-box",
            px: { xs: 2.5, md: 4 }, py: { xs: 6, md: 8 },
        }}>
            <Box sx={{ width: "100%", maxWidth: "960px" }}>
                <Typography component="h1" sx={{
                    fontSize: { xs: 29, md: 40 }, fontWeight: 600, color: Grey17,
                    lineHeight: 1.15, maxWidth: "26ch",
                }}>
                    GxP documents that live in the quality system, not beside it.
                </Typography>

                <Typography sx={{
                    fontSize: { xs: 15, md: 17 }, color: Grey7, mt: 4, maxWidth: "64ch",
                    lineHeight: 1.6,
                }}>
                    Procedures, work instructions, specifications and protocols are written here,
                    in the browser, against the record that governs them — so the text, its
                    revision, its approvals and its audit trail are one thing rather than four.
                </Typography>

                <Button variant="contained" disableElevation onClick={signIn} sx={{
                    mt: { xs: 6, md: 7 }, textTransform: "none", fontSize: 15, fontWeight: 500,
                    px: 5, py: 2, backgroundColor: Blue4, "&:hover": { backgroundColor: Blue3 },
                }}>
                    Sign in
                </Button>

                {/* CARDS WRAPPING RATHER THAN A FIXED GRID, so the page is readable at a
                    phone's width without a second layout to maintain. */}
                <Box sx={{
                    display: "flex", flexWrap: "wrap", gap: 4, mt: { xs: 7, md: 9 },
                }}>
                    {DOES.map((item) => (
                        <Paper key={item.title} variant="outlined" sx={{
                            // BORDER-BOX AGAIN, for the reason the container needs it: with
                            // no global rule a 220px basis plus 20px of padding each side
                            // is a 260px card, four of those do not fit the measure, and
                            // the fourth wrapped onto a row of its own at full width.
                            flex: "1 1 220px", minWidth: 0, p: 5, boxSizing: "border-box",
                            borderColor: Grey3, backgroundColor: Grey2, boxShadow: "none",
                        }}>
                            <Typography sx={{ fontSize: 15, fontWeight: 600, color: Grey17 }}>
                                {item.title}
                            </Typography>
                            <Typography sx={{ fontSize: 13.5, color: Grey7, mt: 2, lineHeight: 1.6 }}>
                                {item.text}
                            </Typography>
                        </Paper>
                    ))}
                </Box>

                {/* THE CATALOGUE. Status on every entry, because a roadmap openly labelled is not
                    a promise and a feature list with the future quietly mixed in is. */}
                <Box sx={{ mt: { xs: 10, md: 12 } }}>
                    <Typography component="h2" sx={{ fontSize: { xs: 22, md: 26 }, fontWeight: 600,
                                                     color: Grey17 }}>
                        Everything it does, and everything it is going to
                    </Typography>
                    <Typography sx={{ fontSize: 13.5, color: Grey7, mt: 2, maxWidth: "70ch",
                                      lineHeight: 1.6 }}>
                        Marked as it stands today. Nothing here is a promise about a date — what is
                        built is in your hands now, and what is not says so.
                    </Typography>

                    {CATALOGUE.map(group => (
                        <Box key={group.title} sx={{ mt: { xs: 8, md: 9 } }}>
                            <Typography sx={{ fontSize: 16, fontWeight: 600, color: Blue4 }}>
                                {group.title}
                            </Typography>
                            <Typography sx={{ fontSize: 13, color: Grey5, mt: 1 }}>
                                {group.blurb}
                            </Typography>

                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 4, mt: 5 }}>
                                {group.features.map(feature => {
                                  // ONLY A TILE WITH A PAGE BEHIND IT IS A LINK. A card that opens
                                  // an empty frame is worse than one that does nothing, so the
                                  // registry is asked rather than assumed -- which lets the stories
                                  // be written one at a time.
                                  const story = storyFor(feature.name);
                                  return (
                                    <Paper key={feature.name} variant="outlined"
                                      onClick={story ? () => navigate(`/feature/${story.slug}`) : undefined}
                                      sx={{
                                        flex: "1 1 260px", minWidth: 0, p: 5,
                                        boxSizing: "border-box", borderColor: Grey3,
                                        backgroundColor: feature.status === "built" ? Grey2 : White,
                                        boxShadow: "none",
                                        ...(story ? {
                                            cursor: "pointer",
                                            transition: "border-color .15s, box-shadow .15s",
                                            "&:hover": { borderColor: Blue3,
                                                         boxShadow: "0 1px 6px rgba(32,33,36,.12)" },
                                        } : {}),
                                    }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                            <Box sx={{ color: feature.status === "built"
                                                ? Blue3 : Grey5, display: "flex" }}>
                                                {feature.icon}
                                            </Box>
                                            <Typography sx={{ fontSize: 14, fontWeight: 600,
                                                              color: Grey17, flexGrow: 1 }}>
                                                {feature.name}
                                            </Typography>
                                        </Box>
                                        <Typography sx={{ fontSize: 13, color: Grey7, mt: 2,
                                                          lineHeight: 1.6 }}>
                                            {feature.text}
                                        </Typography>
                                        <Chip label={LABEL[feature.status]} size="small" sx={{
                                            mt: 3, height: 20, fontSize: 11,
                                            backgroundColor: feature.status === "built"
                                                ? "#E6F4EA"
                                                : feature.status === "building" ? "#FEF7E0" : "transparent",
                                            border: feature.status === "planned"
                                                ? `1px solid ${Grey3}` : "none",
                                            color: feature.status === "built"
                                                ? "#137333"
                                                : feature.status === "building" ? "#8A6D00" : Grey5,
                                        }}/>
                                    </Paper>
                                  );
                                })}
                            </Box>
                        </Box>
                    ))}
                </Box>

                <Typography sx={{ fontSize: 12.5, color: Grey5, mt: { xs: 7, md: 8 } }}>
                    Part of the ParkourAI suite. Sign in with the account your organisation gave
                    you; your persona decides what you can open.
                </Typography>
            </Box>
        </Box>
    );
}
